import {
  Box, Paper, Typography, Stack, Chip, IconButton,
  Button, TextField, Select, MenuItem, FormControl, InputLabel,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Tooltip, CircularProgress
} from "@mui/material";
import DeleteIcon    from "@mui/icons-material/Delete";
import EditIcon      from "@mui/icons-material/Edit";
import AddIcon       from "@mui/icons-material/Add";
import ClearAllIcon  from "@mui/icons-material/ClearAll";
import SmartToyIcon  from "@mui/icons-material/AutoAwesome";
import { useState, useEffect, useCallback } from "react";

const API        = "http://localhost:5099/api/admin/skills";
const SKILL_TYPES = ["global", "ticket", "sheets", "db_query"];
const TYPE_META   = {
  global:   { color: "primary",   label: "global"   },
  ticket: { color: "secondary", label: "ticket" },
  sheets:  { color: "success",   label: "sheets"  },
  db_query:      { color: "warning",   label: "db_query"      },
};

function Toast({ toast }) {
  if (!toast) return null;
  return (
    <Box sx={{
      position: "fixed", top: 20, right: 20, zIndex: 9999,
      background: toast.ok ? "#4CAF50" : "#D32F2F",
      color: "#fff", borderRadius: 2, px: 3, py: 1.5,
      boxShadow: 4, fontSize: 14, fontWeight: 500
    }}>
      {toast.msg}
    </Box>
  );
}

function SkillDialog({ open, mode, form, setForm, onClose, onSubmit }) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>
        {mode === "add" ? "Add global skill" : "Edit global skill"}
      </DialogTitle>
      <DialogContent>
        <FormControl fullWidth sx={{ mt: 1, mb: 2 }}>
          <InputLabel>Skill type</InputLabel>
          <Select
            value={form.skillType}
            label="Skill type"
            onChange={e => setForm(f => ({ ...f, skillType: e.target.value }))}
          >
            {SKILL_TYPES.map(t => (
              <MenuItem key={t} value={t}>
                {t.charAt(0).toUpperCase() + t.slice(1)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          fullWidth multiline rows={4}
          label="Instruction"
          placeholder="e.g. always treat printer issues as Critical priority"
          value={form.instruction}
          onChange={e => setForm(f => ({ ...f, instruction: e.target.value }))}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} color="inherit">Cancel</Button>
        <Button
          onClick={onSubmit}
          variant="contained"
          disabled={!form.instruction.trim()}
          sx={{ background: "linear-gradient(135deg, #1E88E5, #00BCD4)" }}
        >
          {mode === "add" ? "Add skill" : "Save changes"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function AdminPage() {
  const [skills,     setSkills]     = useState([]);
  const [loading,    setLoading]    = useState(false);
  const [filterType, setFilterType] = useState("all");
  const [modal,      setModal]      = useState(null);
  const [editTarget, setEditTarget] = useState(null);
  const [form,       setForm]       = useState({ skillType: "behaviour", instruction: "" });
  const [toast,      setToast]      = useState(null);

  const showToast = (msg, ok = true) => {
    setToast({ msg, ok });
    setTimeout(() => setToast(null), 3000);
  };

  const loadSkills = useCallback(async () => {
    setLoading(true);
    try {
      const res  = await fetch(API);
      const data = await res.json();
      setSkills(data.skills || []);
    } catch { showToast("Failed to load skills", false); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadSkills(); }, [loadSkills]);

  const handleAdd = async () => {
    try {
      const res  = await fetch(API, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ intent: form.skillType, instruction: form.instruction.trim() })
      });
      const data = await res.json();
      if (data.success) {
        showToast("Skill added — MCP updated instantly");
        setModal(null);
        setForm({ skillType: "behaviour", instruction: "" });
        loadSkills();
      } else showToast(data.message, false);
    } catch { showToast("Failed to add skill", false); }
  };

  const openEdit = (skill) => {
    setEditTarget(skill);
    setForm({ skillType: skill.skillType, instruction: skill.instruction });
    setModal("edit");
  };

  const handleEdit = async () => {
    try {
      const res  = await fetch(`${API}/${editTarget.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ intent: form.skillType, instruction: form.instruction.trim() })
      });
      const data = await res.json();
      if (data.success) {
        showToast("Skill updated — MCP updated instantly");
        setModal(null);
        loadSkills();
      } else showToast(data.message, false);
    } catch { showToast("Failed to update skill", false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this skill?")) return;
    try {
      const res  = await fetch(`${API}/${id}`, { method: "DELETE" });
      const data = await res.json();
      if (data.success) {
        showToast("Skill deleted");
        loadSkills();
      } else showToast(data.message, false);
    } catch { showToast("Failed to delete", false); }
  };

  const handleClearAll = async () => {
    if (!window.confirm("Clear ALL global MCP skills?")) return;
    try {
      // Delete each one
      await Promise.all(skills.map(s => fetch(`${API}/${s.id}`, { method: "DELETE" })));
      showToast("All skills cleared");
      loadSkills();
    } catch { showToast("Failed to clear all", false); }
  };

  const filtered = skills.filter(s => filterType === "all" || s.skillType === filterType);

  return (
    <Box sx={{ height: "100vh", display: "flex", flexDirection: "column", background: "#f0f4f8" }}>
      <Toast toast={toast} />

      {/* Header */}
      <Box sx={{
        height: 64, px: 3, display: "flex", alignItems: "center",
        justifyContent: "space-between",
        background: "linear-gradient(135deg, #1E88E5, #00BCD4)", color: "#fff"
      }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <SmartToyIcon sx={{ color: "#fff" }} />
          <Typography variant="h6" fontWeight={800} letterSpacing={1}>
            INFOMAZE — MCP Skills
          </Typography>
        </Stack>
        <Button variant="outlined" size="small" href="/"
          sx={{ color: "#fff", borderColor: "rgba(255,255,255,0.5)" }}>
          ← Back to Chat
        </Button>
      </Box>

      {/* Subheader — actions + filter */}
      <Paper elevation={0} sx={{
        px: 3, py: 1.5, borderBottom: "1px solid #e0e0e0",
        display: "flex", alignItems: "center",
        justifyContent: "space-between", borderRadius: 0
      }}>
        {/* Filter chips */}
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="body2" color="text.secondary" mr={1}>
            Filter:
          </Typography>
          {["all", ...SKILL_TYPES].map(t => (
            <Chip
              key={t}
              label={`${t.charAt(0).toUpperCase() + t.slice(1)}${
                t !== "all"
                  ? ` (${skills.filter(s => s.skillType === t).length})`
                  : ` (${skills.length})`
              }`}
              onClick={() => setFilterType(t)}
              color={filterType === t ? (TYPE_META[t]?.color || "primary") : "default"}
              variant={filterType === t ? "filled" : "outlined"}
              size="small"
              sx={{ cursor: "pointer" }}
            />
          ))}
        </Stack>

        {/* Action buttons */}
        <Stack direction="row" spacing={1}>
          {skills.length > 0 && (
            <Button
              startIcon={<ClearAllIcon />}
              color="error" variant="outlined" size="small"
              onClick={handleClearAll}
            >
              Clear all
            </Button>
          )}
          <Button
            startIcon={<AddIcon />}
            variant="contained" size="small"
            onClick={() => {
              setForm({ skillType: "behaviour", instruction: "" });
              setModal("add");
            }}
            sx={{ background: "linear-gradient(135deg, #1E88E5, #00BCD4)" }}
          >
            Add skill
          </Button>
        </Stack>
      </Paper>

      {/* Skills list */}
      <Box sx={{ flex: 1, overflowY: "auto", p: 3 }}>
        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", pt: 6 }}>
            <CircularProgress />
          </Box>
        )}

        {!loading && filtered.length === 0 && (
          <Box sx={{ textAlign: "center", pt: 8 }}>
            <SmartToyIcon sx={{ fontSize: 48, color: "#ccc", mb: 2 }} />
            <Typography color="text.secondary" mb={3}>
              {filterType !== "all"
                ? `No ${filterType} skills yet`
                : "No global MCP skills yet — add one to start training the MCP"}
            </Typography>
            <Button
              startIcon={<AddIcon />}
              variant="contained"
              onClick={() => {
                setForm({ skillType: "behaviour", instruction: "" });
                setModal("add");
              }}
              sx={{ background: "linear-gradient(135deg, #1E88E5, #00BCD4)" }}
            >
              Add first skill
            </Button>
          </Box>
        )}

        <Stack spacing={1.5} maxWidth={860} mx="auto">
          {filtered.map(skill => (
            <Paper key={skill.id} sx={{
              p: 2, borderRadius: 2,
              border: "1px solid #e0e0e0",
              display: "flex", alignItems: "flex-start", gap: 2
            }}>
              <Box flex={1}>
                <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                  <Chip
                    label={TYPE_META[skill.skillType]?.label || skill.skillType}
                    color={TYPE_META[skill.skillType]?.color || "default"}
                    size="small"
                  />
                  <Typography variant="caption" color="text.secondary">
                    Added {skill.createdAt}
                    {skill.updatedAt !== skill.createdAt && ` · Edited ${skill.updatedAt}`}
                  </Typography>
                </Stack>
                <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                  {skill.instruction}
                </Typography>
              </Box>
              <Stack direction="row" spacing={0.5} flexShrink={0}>
                <Tooltip title="Edit">
                  <IconButton size="small" onClick={() => openEdit(skill)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete">
                  <IconButton size="small" color="error" onClick={() => handleDelete(skill.id)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Paper>
          ))}
        </Stack>
      </Box>

      <SkillDialog
        open={!!modal}
        mode={modal}
        form={form}
        setForm={setForm}
        onClose={() => setModal(null)}
        onSubmit={modal === "add" ? handleAdd : handleEdit}
      />
    </Box>
  );
}